import { Accordion, AccordionDetails, AccordionSummary, Chip, Grid, IconButton, Tooltip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { DeleteRounded, ExpandMore, HealthAndSafety } from "@mui/icons-material";
import { Controller, useFormContext, useWatch} from "react-hook-form";
import { useRecoilState, useSetRecoilState } from "recoil";
import { useQuery } from "@tanstack/react-query";
import { useParams, useSearchParams } from "react-router-dom";
import { FormAutocomplete, FormCheckbox, FormDateTimePicker, FormTextField } from "../../../../components/fields";
import { accordionExpandedState, visibleCarToReplaceAccordion  } from "../../recoil";
import { IFormValues, MAX_CHAR_TEXTAREA, substitutionReasonValue } from "../../types";
import GoogleAutocompleteWrapper from "../../../../../../util/component/GoogleAutocompleteWrapper";
import { getAddressFromApiGoogle } from "../../../../utils";
import { ItCodeCountry, ReactQueryKey, RouteParams } from "../../../../types";
import { getCountries, useCountriesQueryObserver, getProvinces, useBookingSubstitutionQueryObserver } from "../../../../api/services";
import { isBusinessContinuityMode } from "../../../insert/recoil";
import { ITALY_CODE } from "../../../../../network/config";

const AddressCountryField = (props: {disabled: boolean}) => {
    const { t } = useTranslation('booking');

    const countriesQuery = useQuery({
        queryKey: [ReactQueryKey.Countries],
        queryFn: getCountries,
        refetchOnMount: false
    });

    return (
        <FormAutocomplete<IFormValues>
            name="eventInfo.eventPlaceCountry"
            label={(t('label.eventInfo_eventPlaceCountry') + '*')}
            options={countriesQuery.data ?? []}
            disabled={props.disabled}
            loading={countriesQuery.isInitialLoading}
        />
    );
}
const ProvinceField = (props: {disabled: boolean}) => {
    const { t } = useTranslation('booking');

    const country = useWatch<IFormValues, 'eventInfo.eventPlaceCountry'>({
        name: 'eventInfo.eventPlaceCountry'
    })

    const provincesQuery = useQuery({
        queryKey: [ReactQueryKey.Provinces, {
            country: country,
            refetchOnMount: false
        }],
        queryFn: () => {
            if (!country || country === ItCodeCountry) {
                return null;
            }
            return getProvinces({
                country: country
            })
        },
        enabled: country !== ItCodeCountry,
        refetchOnMount: false
    });

    function getRequiredIndicator() {
        if (country === ITALY_CODE) {
            return "*";
        }

        return "";
    }

    return (
        <FormAutocomplete<IFormValues>
            name={'eventInfo.eventPlaceProvince'}
            label={(t("label.eventInfo_eventPlaceProvince") + getRequiredIndicator())}
            options={provincesQuery.data ?? []}
            fullWidth
            disabled={props.disabled}
            loading={provincesQuery.isInitialLoading}
        />
    );
}

const EventInfo = () => {
    const { t } = useTranslation('booking');

    const [queryParameters] = useSearchParams();
    const recoveryMode = queryParameters.get('recoveryMode') === '1' || false;

    const today = useMemo(() => dayjs().hour(23).minute(59), [t]);
    // const today = useMemo(() => dayjs(), [t]);   TODO solo nella pagina recovery

    const pickupDateRa = useWatch<IFormValues, "originRaData.pickupDate">({
        name: 'originRaData.pickupDate'
    });
    const dropoffDateRa = useWatch<IFormValues, "originRaData.dropoffDate">({
        name: 'originRaData.dropoffDate'
    });
    const countriesList = useCountriesQueryObserver();
    const { id } = useParams<RouteParams>();
    const substitutionQuery =  useBookingSubstitutionQueryObserver(id);

    const [infoEventExpanded, setInfoEventExpanded] = useRecoilState(accordionExpandedState('eventInfo'));
    const [eventDescriptionLength, setEventDescriptionLength] = useState<number>(0);
    const [notePoliceLength, setNotePoliceLength] = useState<number>(0);
    const [addressPlace, setAddressPlace] = useState<any>(undefined);
    const [vehicleAvailabilityDisabled, setVehicleAvailabilityDisabled] = useState<boolean>(false);
    const [businessContinuityMode, setBusinessContinuityMode] = useRecoilState(isBusinessContinuityMode);
    const [countryDisable, setCountryDisable] = useState<boolean>(true);
    const [cityDisable, setCityDisable] = useState<boolean>(true);
    const [addressDisable, setAddressDisable] = useState<boolean>(true);
    const [streetNumberDisable, setStreetNumberDisable] = useState<boolean>(true);
    const [postcodeDisable, setPostcodeDisable] = useState<boolean>(true);
    const [provinceDisable, setProvinceDisable] = useState<boolean>(true);
    const setCarToReplaceAccordion = useSetRecoilState(visibleCarToReplaceAccordion);
    const formContext = useFormContext<IFormValues>();
    const reason = useWatch<IFormValues, "eventInfo.reason">({
        name: 'eventInfo.reason'
    });
    const vehicleAvailability = useWatch<IFormValues, "eventInfo.vehicleAvailability">({
        name: 'eventInfo.vehicleAvailability'
    });

    const maxChar = MAX_CHAR_TEXTAREA;

    const googleInputPropsEventPlaceField = {
        onPlaceSelected: (place: any) => selectedAddress(place),
        options: {fields: ['address_components'], types: ['address']}
    }

    useEffect(() => {
        const now = dayjs();
        const dropOff = dayjs(dropoffDateRa);
        if(recoveryMode && dropoffDateRa && dropOff.isBefore(now)){
            formContext.setValue('eventInfo.eventDate',dropoffDateRa)
        }  
    }, [recoveryMode,dropoffDateRa])
    

    const selectedAddress = useCallback((result : any) => {setAddressPlace(result)}, []);

    const onChangeSubstitutionReason = useCallback(() => {
        const reasonValue = formContext.getValues('eventInfo.reason');
        if([substitutionReasonValue.FURTO_VEICOLO, substitutionReasonValue.INCENDIO].includes(reasonValue)) {
            formContext.setValue('eventInfo.vehicleAvailability', false);
            if(reasonValue === substitutionReasonValue.FURTO_VEICOLO) setVehicleAvailabilityDisabled(true);
            else setVehicleAvailabilityDisabled(false);
        } else if([substitutionReasonValue.REMARKETING, substitutionReasonValue.CAMBIO_TARGA].includes(reasonValue)) {
            formContext.setValue('eventInfo.vehicleAvailability', true);
            setVehicleAvailabilityDisabled(true);
        } else if (!!reasonValue) {
            formContext.setValue('eventInfo.vehicleAvailability', true);
            setVehicleAvailabilityDisabled(false);
        } else {
            setVehicleAvailabilityDisabled(false);
        }
        if(![substitutionReasonValue.INCIDENTE_CON_CONTROPARTE, substitutionReasonValue.INCIDENTE_SENZA_CONTROPARTE].includes(reasonValue)) {
            formContext.resetField('eventInfo.substancesInfluence');
        } else {
            formContext.setValue('eventInfo.substancesInfluence', false);
        }
        if (![substitutionReasonValue.INCIDENTE_CON_CONTROPARTE, substitutionReasonValue.INCIDENTE_SENZA_CONTROPARTE, substitutionReasonValue.INCENDIO].includes(reasonValue)) {
            formContext.resetField('eventInfo.policeInvolved');
        } else {
            formContext.setValue('eventInfo.policeInvolved', false);
            formContext.resetField('eventInfo.notePoliceInvolved');
            setNotePoliceLength(0);
        }
    }, [formContext]);

    /** reset value place address data **/
    const handleResetAddressFields = useCallback(() => {
        formContext.resetField('eventInfo.eventPlaceGoogleAddress');
        formContext.resetField('eventInfo.eventPlaceCountry');
        formContext.resetField('eventInfo.eventPlaceProvince');
        formContext.resetField('eventInfo.eventPlaceCity');
        formContext.resetField('eventInfo.eventPlacePostalCode');
        formContext.resetField('eventInfo.eventPlaceStreetName');
        formContext.resetField('eventInfo.eventPlaceStreetNumber');
        setCountryDisable(true);
        setCityDisable(true);
        setAddressDisable(true);
        setStreetNumberDisable(true);
        setPostcodeDisable(true);
        setProvinceDisable(true);
    }, [formContext]);

    /** dep: addressPlace, set form value field of address  **/
    useEffect(() => {
        if(addressPlace) {
            const filters = ['country', 'locality', 'route', 'street_number', 'postal_code', 'administrative_area_level_2'];
            const [country, locality, route, street_number, postal_code, administrative_area_level_2] = getAddressFromApiGoogle(addressPlace?.address_components, filters);

            formContext.setValue('eventInfo.eventPlaceCity', locality?.short_name ?? '');
            setCityDisable(!!locality?.short_name);

            formContext.setValue('eventInfo.eventPlaceStreetName', route?.short_name ?? '');
            setAddressDisable(!!route?.short_name);

            formContext.setValue('eventInfo.eventPlaceStreetNumber', street_number?.short_name ?? '' );
            setStreetNumberDisable(!!street_number?.short_name);

            if(!!route?.short_name) {
                formContext.setValue('eventInfo.eventPlaceGoogleAddress', route?.short_name ?? '' );
            }

            formContext.setValue('eventInfo.eventPlacePostalCode', postal_code?.short_name ?? '' );
            setPostcodeDisable(!!postal_code?.short_name);

            if(countriesList?.data?.includes(country?.short_name)) {
                formContext.setValue('eventInfo.eventPlaceCountry',  country.short_name);
                setCountryDisable(true);
                if(country.short_name === ItCodeCountry) {
                    formContext.setValue('eventInfo.eventPlaceProvince', administrative_area_level_2?.short_name);
                    setProvinceDisable(!!administrative_area_level_2?.short_name);
                } else {
                    formContext.resetField('eventInfo.eventPlaceProvince');
                    setProvinceDisable(false);
                }
            } else {
                formContext.resetField('eventInfo.eventPlaceCountry');
                setCountryDisable(false);
                formContext.resetField('eventInfo.eventPlaceProvince');
                setProvinceDisable(false);
            }
        }
    }, [addressPlace]);

    useEffect(() => {
        setCarToReplaceAccordion(!!vehicleAvailability);
        formContext.resetField('carToReplace');
    }, [vehicleAvailability]);

     useEffect(() => {
                setBusinessContinuityMode(window.location.pathname.includes('request-recovery-substitution'));
            }, [window.location]);

    return(
        <Accordion expanded={infoEventExpanded} onChange={(e, expanded) => setInfoEventExpanded(expanded)}>
            <AccordionSummary expandIcon={<ExpandMore />}
                              sx={{
                                  '.MuiAccordionSummary-content': {
                                      justifyContent: "space-between",
                                      alignItems: "center"
                                  }
                              }}
            >
                <Typography variant="h5">
                    {t('label.eventInfo_title')}
                </Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Grid item container rowSpacing={1} columnSpacing={3}>
                    <Grid item xs={12} sm={3}>
                        <FormAutocomplete<IFormValues>
                            name="eventInfo.reason"
                            label={(t('label.eventInfo_reason') + '*')}
                            options={substitutionQuery?.data?.substitutionReason ?? []}
                            onChange={onChangeSubstitutionReason}
                            isOptionEqualToValue={(option: any, value: any) => option === value}
                            getOptionLabel={(option: string) => { return !!option && t(`substitution_reason.${option}`) ? t(`substitution_reason.${option}`) : ''}}
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        
                        <FormDateTimePicker<IFormValues>
                            name="eventInfo.eventDate"
                            label={(t('label.eventInfo_eventDate') + '*')}
                            maxDateTime={today.isBefore(dropoffDateRa) ? today : dropoffDateRa}
                            minDateTime={pickupDateRa}
                        />
                    </Grid>
                    <Grid item xs={11} sm={11}>
                        <Controller
                            name={'eventInfo.eventPlaceGoogleAddress'}
                            control={formContext.control}
                            render={({field: {onChange, value}}: any) => (
                                <GoogleAutocompleteWrapper
                                    googleInputProps={googleInputPropsEventPlaceField}
                                    inputProps={{
                                        fullWidth: true,
                                        label: t('label.eventInfo_eventPlace'),
                                        id: 'addressGoogleSearch',
                                        name: 'eventInfo.eventPlaceGoogleAddress',
                                        type: 'text',
                                        value: value || '',
                                        onChange: (e) => {
                                            onChange(e.target.value)
                                            if(!e.target.value) {handleResetAddressFields()}
                                        }
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={1} sm={1} alignItems="center"
                          sx={{textAlign: 'center', marginTop: 'auto'}}>
                        <Tooltip arrow placement={"top"} title={t("label.eventInfo_resetPlace")}>
                            <IconButton onClick={handleResetAddressFields}>
                                <DeleteRounded fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <AddressCountryField disabled={countryDisable} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <ProvinceField disabled={provinceDisable} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FormTextField<IFormValues>
                            name={'eventInfo.eventPlaceCity'}
                            label={(t("label.eventInfo_eventPlaceCity") + '*')}
                            disabled={cityDisable}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FormTextField<IFormValues>
                            name={'eventInfo.eventPlacePostalCode'}
                            label={(t("label.eventInfo_eventPlacePostalCode") + '*')}
                            disabled={postcodeDisable}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FormTextField<IFormValues>
                            name={'eventInfo.eventPlaceStreetName'}
                            label={(t("label.eventInfo_eventPlaceStreetName") + '*')}
                            disabled={addressDisable}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FormTextField<IFormValues>
                            name={'eventInfo.eventPlaceStreetNumber'}
                            label={(t("label.eventInfo_eventPlaceStreetNumber") + '*')}
                            disabled={streetNumberDisable}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormTextField
                            onChange={(e) => {setEventDescriptionLength(e.target.value.length);}}
                            control={formContext.control}
                            name="eventInfo.eventDescription"
                            label={(t("label.eventInfo_eventDescription") + '*')}
                            placeholder={t('label.eventInfo_eventDescriptionPlaceholder')}
                            multiline
                            rows={2}
                            error={eventDescriptionLength > maxChar}
                            helperText={eventDescriptionLength + ' / ' + maxChar}
                            FormHelperTextProps={{
                                component: "div"
                            } as any}
                        />
                    </Grid>
                    <Grid item xs={12} sm={3} mt={2} justifyContent="space-between" alignItems="center">
                        <FormCheckbox<IFormValues>
                            name="eventInfo.vehicleAvailability"
                            label={t('label.eventInfo_vehicleAvailability')}
                            disabled={vehicleAvailabilityDisabled}
                        />
                    </Grid>
                    { [substitutionReasonValue.INCIDENTE_CON_CONTROPARTE, substitutionReasonValue.INCIDENTE_SENZA_CONTROPARTE].includes(reason) &&
                        <Grid item xs={12} sm={3}  mt={2} justifyContent="space-between" alignItems="center">
                            <FormCheckbox<IFormValues>
                                name="eventInfo.substancesInfluence"
                                label={t('label.eventInfo_substancesInfluence')}
                            />
                        </Grid>
                    }
                    { [substitutionReasonValue.INCIDENTE_CON_CONTROPARTE, substitutionReasonValue.INCIDENTE_SENZA_CONTROPARTE, substitutionReasonValue.INCENDIO].includes(reason) &&
                        <>
                            <Grid item xs={12} sm={3}  mt={2} justifyContent="space-between" alignItems="center">
                                <FormCheckbox<IFormValues>
                                    name="eventInfo.policeInvolved"
                                    label={t('label.eventInfo_policeInvolved')}
                                    onChange={() => {
                                        formContext.resetField('eventInfo.notePoliceInvolved');
                                        setNotePoliceLength(0);
                                    }}
                                />
                            </Grid>
                            { formContext.getValues('eventInfo.policeInvolved') &&
                                <Grid item xs={12}>
                                    <FormTextField
                                        onChange={(e: any) => {
                                            setNotePoliceLength(e.target.value.length);
                                        }}
                                        control={formContext.control}
                                        name="eventInfo.notePoliceInvolved"
                                        label={(t("label.eventInfo_notePoliceInvolved")  + '*')}
                                        placeholder={t('label.eventInfo_notePoliceInvolved_placeholder')}
                                        multiline
                                        rows={2}
                                        error={notePoliceLength > maxChar}
                                        helperText={notePoliceLength + ' / ' + maxChar}
                                        FormHelperTextProps={{
                                            component: "div"
                                        } as any}
                                    />
                                </Grid>
                            }
                        </>
                    }
                 </Grid>
            </AccordionDetails>
        </Accordion>
    );
}

export default EventInfo;
